/**
 * Main-thread client for the analysis worker.
 *
 * `segmentRegionsInWorker` is the per-sheet API the orchestrator
 * calls. It transfers the sheet's Layer 0 typed-array buffers OUT to
 * the worker, awaits the regions result, and transfers the inputs
 * back along with the new regionOfCell array. The main thread is
 * free to do other work while the worker computes.
 *
 * The transfer-out / transfer-back pattern matters because Layer 0
 * arrays are still needed on the main thread after Layer 1 finishes
 * (rules read from them). Without transferring back, they'd remain
 * detached on the main side.
 *
 * One Worker is reused across all calls (lazy-created on first
 * request). Failure mode: if the Worker errors, all pending jobs
 * reject and the next request creates a fresh Worker.
 */

import type { AnomalyRecord, Region, SheetBounds } from "../types";

export interface SegmentRegionsResult {
  regions: Region[];
  regionOfCell: Int32Array;
  cellOrigin: Int8Array;
  rowDensity: Int32Array;
  colDensity: Int32Array;
}

export interface AnalyzePatternsResult {
  anomalyMask: Uint8Array;
  anomalies: AnomalyRecord[];
  cellOrigin: Int8Array;
  cellValueKind: Int8Array;
  r1c1Ids: Int32Array;
}

interface PendingJob {
  resolve: (result: unknown) => void;
  reject: (err: Error) => void;
}

interface RegionsComputedMsg {
  type: "regionsComputed";
  jobId: number;
  regions: Region[];
  regionOfCellBuffer: ArrayBuffer;
  cellOriginBuffer: ArrayBuffer;
  rowDensityBuffer: ArrayBuffer;
  colDensityBuffer: ArrayBuffer;
}

interface PatternsComputedMsg {
  type: "patternsComputed";
  jobId: number;
  anomalyMaskBuffer: ArrayBuffer;
  anomalies: AnomalyRecord[];
  cellOriginBuffer: ArrayBuffer;
  cellValueKindBuffer: ArrayBuffer;
  r1c1IdsBuffer: ArrayBuffer;
}

interface JobErrorMsg {
  type: "jobError";
  jobId: number;
  reason: string;
}

type WorkerResponse = RegionsComputedMsg | PatternsComputedMsg | JobErrorMsg;

let worker: Worker | null = null;
let nextJobId = 1;
const pending = new Map<number, PendingJob>();

function ensureWorker(): Worker {
  if (worker !== null) return worker;

  worker = new Worker(new URL("./worker.ts", import.meta.url));

  worker.addEventListener("message", (e: MessageEvent<WorkerResponse>) => {
    const msg = e.data;
    const job = pending.get(msg.jobId);
    if (!job) return;
    pending.delete(msg.jobId);

    if (msg.type === "regionsComputed") {
      const result: SegmentRegionsResult = {
        regions: msg.regions,
        regionOfCell: new Int32Array(msg.regionOfCellBuffer),
        cellOrigin: new Int8Array(msg.cellOriginBuffer),
        rowDensity: new Int32Array(msg.rowDensityBuffer),
        colDensity: new Int32Array(msg.colDensityBuffer),
      };
      job.resolve(result);
    } else if (msg.type === "patternsComputed") {
      const result: AnalyzePatternsResult = {
        anomalyMask: new Uint8Array(msg.anomalyMaskBuffer),
        anomalies: msg.anomalies,
        cellOrigin: new Int8Array(msg.cellOriginBuffer),
        cellValueKind: new Int8Array(msg.cellValueKindBuffer),
        r1c1Ids: new Int32Array(msg.r1c1IdsBuffer),
      };
      job.resolve(result);
    } else if (msg.type === "jobError") {
      job.reject(new Error(`worker job ${msg.jobId} failed: ${msg.reason}`));
    }
  });

  worker.addEventListener("error", (e: ErrorEvent) => {
    // Worker died — reject everything in flight and discard so the
    // next call spins up a fresh one.
    const reason = e.message || "worker errored";
    for (const job of Array.from(pending.values())) {
      job.reject(new Error(`worker crashed: ${reason}`));
    }
    pending.clear();
    worker = null;
  });

  return worker;
}

/**
 * Run Layer 1 segmentRegions on the worker. The three input typed
 * arrays are transferred (their buffers detach on the main thread for
 * the duration of the call); they come back on resolve, along with
 * the newly-computed `regions` list and `regionOfCell` array.
 *
 * The caller must replace its own references to `cellOrigin`,
 * `rowDensity`, `colDensity` with the result's versions — same
 * underlying data, but the buffers may be different ArrayBuffer
 * instances after the transfer round-trip.
 */
export async function segmentRegionsInWorker(
  bounds: SheetBounds,
  cellOrigin: Int8Array,
  rowDensity: Int32Array,
  colDensity: Int32Array
): Promise<SegmentRegionsResult> {
  const w = ensureWorker();
  const jobId = nextJobId++;

  return new Promise<SegmentRegionsResult>((resolve, reject) => {
    pending.set(jobId, {
      resolve: resolve as (r: unknown) => void,
      reject,
    });
    w.postMessage(
      {
        type: "segmentRegions",
        jobId,
        bounds,
        cellOriginBuffer: cellOrigin.buffer,
        rowDensityBuffer: rowDensity.buffer,
        colDensityBuffer: colDensity.buffer,
      },
      [cellOrigin.buffer, rowDensity.buffer, colDensity.buffer]
    );
  });
}

/**
 * Run Layer 2 analyzePatterns on the worker. Transfers cellOrigin,
 * cellValueKind, and r1c1Ids out; they come back along with the new
 * anomalyMask buffer and the anomalies list. Same buffer-replacement
 * rule as segmentRegionsInWorker — replace the SheetModel's references
 * with the result's after the call returns.
 */
export async function analyzePatternsInWorker(
  bounds: SheetBounds,
  regions: Region[],
  cellOrigin: Int8Array,
  cellValueKind: Int8Array,
  r1c1Ids: Int32Array
): Promise<AnalyzePatternsResult> {
  const w = ensureWorker();
  const jobId = nextJobId++;

  return new Promise<AnalyzePatternsResult>((resolve, reject) => {
    pending.set(jobId, {
      resolve: resolve as (r: unknown) => void,
      reject,
    });
    w.postMessage(
      {
        type: "analyzePatterns",
        jobId,
        bounds,
        regions,
        cellOriginBuffer: cellOrigin.buffer,
        cellValueKindBuffer: cellValueKind.buffer,
        r1c1IdsBuffer: r1c1Ids.buffer,
      },
      [cellOrigin.buffer, cellValueKind.buffer, r1c1Ids.buffer]
    );
  });
}

/**
 * Tear down the worker. Useful for tests, or for tooling that wants
 * to reclaim the worker thread when audit is idle. Pending jobs
 * reject. The next call to segmentRegionsInWorker will create a
 * fresh worker.
 */
export function disposeAnalysisWorker(): void {
  if (worker === null) return;
  for (const job of Array.from(pending.values())) {
    job.reject(new Error("analysis worker disposed"));
  }
  pending.clear();
  worker.terminate();
  worker = null;
}
